import { FeatureFlag, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import { useRouter } from 'next/compat/router'
import { BiTrendingDown } from 'react-icons/bi'
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi'
import { MdFormatListBulleted } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'

import type { IconButtonProps } from '@chakra-ui/react'
import { Box, Flex, Grid, Icon, IconButton, Square } from '@chakra-ui/react'
import styled from '@emotion/styled'

import {
  AiOperator,
  EmergencyCall,
  Gateway,
  Logo,
  Rule,
} from '@/components/icons'
import {
  CaseManagement,
  Dashboard,
  Facility,
  Globe,
  GuardTour,
  Incident,
  SecurityDevice,
} from '@/components/icons'
import { selectSideNavOpen, toggleSideNavOpen } from '@/redux/ui/uiSlice'
import { SIDE_BAR_WIDTH_CLOSED, SIDE_BAR_WIDTH_OPEN } from '@/styles/theme'
import { mixpanel } from '@/utils/analytics'
import {
  AI_OPERATOR_HUB,
  CASE_MANAGEMENT,
  DASHBOARD,
  DEVICES,
  EMERGENCY_CALL_LOG,
  FACILITIES,
  GATEWAYS,
  GLOBAL,
  GUARD_TOURS,
  INCIDENTS,
  INCIDENT_RULES,
  METRICS_DASHBOARD,
  STANDARD_OPERATING_PROCEDURES,
} from '@/utils/routes'

import { SideBarEditMode } from './SideBarEditMode'
import { SideBarNavItem } from './SideBarNavItem'
import { SideBarProfile } from './SideBarProfile'

const SideBarStyles = styled.div<{ $show: boolean }>`
  top: 0;
  left: 0;
  width: ${(p) => (p.$show ? SIDE_BAR_WIDTH_OPEN : SIDE_BAR_WIDTH_CLOSED)};
  background: linear-gradient(180deg, #16357f 0%, #091848 100%);
  box-shadow: 4px 0 14px 0 rgba(65, 71, 95, 0.17);
  transition: ease-in-out 0.3s width;
  z-index: 100;
  min-height: 0;
`

const NavToggle = styled(IconButton as React.ComponentType<IconButtonProps>)`
  position: absolute;
  right: 0;
  top: 30px;
  transform: translate(50%, -50%);
  background-color: #fff;
  font-size: 20px;
  height: 18px;
  min-width: 18px;
`

export const SideBar = () => {
  const dispatch = useDispatch()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const router = useRouter()
  const {
    shouldShowDashboardPage,
    shouldShowEditMode,
    shouldShowSOPPage,
    shouldShowIncidentRulesPage,
    shouldShowCaseManagementPage,
    shouldShowOperatorHubPage,
  } = usePermissions()

  const flags = useFeatureFlags()
  const { hasNoLiveStream, isOrangePeel } = useLiveStreamSetup()
  const shouldShowLegacyDashboard = flags[FeatureFlag.legacyDashboard]
  const isEmergencyCallEnabled = flags[FeatureFlag.emergencyCall]
  const isGuardToursEnabled = flags[FeatureFlag.guardTours]
  const isCaseManagementEnabled = flags[FeatureFlag.caseManagement]
  const isIncidentRulesEnabled = flags[FeatureFlag.incidentRules]
  const isAiOperatorHubEnabled = flags[FeatureFlag.aiOperatorHub]
  const isIPv6RedirectionEnabled = flags[FeatureFlag.ipv6Redirection]

  return (
    <SideBarStyles $show={isSideNavOpen}>
      <Flex
        as='nav'
        direction='column'
        h='100%'
        justifyContent='space-between'
        position='relative'
      >
        <NavToggle
          aria-label='Toggle Sidebar'
          borderRadius='full'
          boxShadow='0px 0px 4px rgba(0, 0, 0, 0.5)'
          data-testid='sidebar_expand_toggle'
          icon={
            <Icon
              as={isSideNavOpen ? HiOutlineChevronLeft : HiOutlineChevronRight}
              boxSize='16px'
              color='#4C535F'
            />
          }
          onClick={() => dispatch(toggleSideNavOpen())}
          size='xs'
        />
        <Grid h='full' templateRows='60px auto'>
          <Box>
            <Grid
              alignItems='center'
              cursor='pointer'
              gap='5px'
              onClick={() => router.push(GLOBAL)}
              overflow='hidden'
              templateColumns='max-content max-content'
            >
              <Square h='60px' w='60px'>
                <Icon as={Logo} boxSize='34px' />
              </Square>
              {isSideNavOpen && (
                <Box
                  color='#fff'
                  fontSize='16px'
                  fontWeight='bold'
                  letterSpacing='-0.65px'
                  lineHeight='20px'
                >
                  HiveWatch
                </Box>
              )}
            </Grid>
          </Box>
          <Flex
            css={{
              '&::-webkit-scrollbar': {
                display: 'none',
                width: 0,
              },
            }}
            flexDirection='column'
            justifyContent='space-between'
            overflowY='auto'
            pt='20px'
          >
            <Box>
              {!isIPv6RedirectionEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_global'
                  icon={Globe}
                  label='Global'
                  path={GLOBAL}
                />
              )}
              <SideBarNavItem
                dataTestId='sidebar_navItem_facilities'
                icon={Facility}
                label='Facilities'
                path={FACILITIES}
              />
              <SideBarNavItem
                dataTestId='sidebar_navItem_incidents'
                icon={Incident}
                label='Incidents'
                path={INCIDENTS}
              />
              {!hasNoLiveStream && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_devices'
                  icon={SecurityDevice}
                  label='Devices'
                  path={DEVICES}
                />
              )}
              {shouldShowDashboardPage &&
                (shouldShowLegacyDashboard ? (
                  <SideBarNavItem
                    dataTestId='sidebar_navItem_metrics'
                    icon={Dashboard}
                    label='Metrics'
                    path={METRICS_DASHBOARD}
                  />
                ) : (
                  <SideBarNavItem
                    dataTestId='sidebar_navItem_dashboard'
                    icon={BiTrendingDown}
                    label='Dashboard'
                    path={DASHBOARD}
                  />
                ))}
              {shouldShowSOPPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_sop'
                  icon={MdFormatListBulleted}
                  label='SOPs'
                  path={STANDARD_OPERATING_PROCEDURES}
                />
              )}
              {isEmergencyCallEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_emergencyCalling'
                  icon={EmergencyCall}
                  label='Emergency Calls'
                  path={EMERGENCY_CALL_LOG}
                />
              )}
              {isGuardToursEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_guardTours'
                  icon={GuardTour}
                  label='Guard Tours'
                  onClick={() =>
                    mixpanel.track('Button Press: Nav - Guard Tours List')
                  }
                  path={GUARD_TOURS}
                />
              )}
              {isOrangePeel && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_gateways'
                  icon={Gateway}
                  label='Gateways'
                  path={GATEWAYS}
                />
              )}
              {isCaseManagementEnabled && shouldShowCaseManagementPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_case_management'
                  icon={CaseManagement}
                  label='Case Management'
                  path={CASE_MANAGEMENT}
                />
              )}

              {isIncidentRulesEnabled && shouldShowIncidentRulesPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_incidentRules'
                  icon={Rule}
                  iconSize='32px'
                  label='Rules'
                  path={INCIDENT_RULES}
                />
              )}

              {isAiOperatorHubEnabled && shouldShowOperatorHubPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_aiOperatorHub'
                  icon={AiOperator}
                  label='AI Operator Hub'
                  path={AI_OPERATOR_HUB}
                />
              )}
            </Box>
            <Box>
              {shouldShowEditMode && <SideBarEditMode />}
              <SideBarProfile />
            </Box>
          </Flex>
        </Grid>
      </Flex>
    </SideBarStyles>
  )
}
